// ABOUTME: Every agent chat's one 56px top bar (and the floating panel's compact
// bar): back chevron, the agent's name, and the conversation controls ChatGPT has — Past chats
// and New chat — before the same HeaderActions every page shows. On a wide screen the two sit
// inline; on a phone they share one menu, since two more 44px icons don't fit beside the app
// actions at 390px. Voice lives in the composer, Read aloud on each reply.
import { useState } from 'react'
import { Coins, History, MoreHorizontal, SquarePen, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { BackToHome } from '@/components/layout/BackToHome'
import { HeaderActions } from '@/components/layout/HeaderActions'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { PastChatsSheet } from './PastChatsSheet'

interface ChatTopBarProps {
  /** The agent's name, and its avatar beside it. */
  title: string
  icon?: React.ReactNode
  variant: 'panel' | 'fullscreen'
  /** Class for the inner row so it rides the same width track as the messages. */
  contentWidth?: string
  creditsRemaining: number | null
  /** Start a new chat; the one before it stays in Past chats. */
  onClear: () => void
  /** A team member's thread; Marketing's when absent. */
  roleId?: string
  onClose?: () => void
}

function IconAction({
  label,
  testId,
  onClick,
  side = 'bottom',
  children,
}: {
  label: string
  testId: string
  onClick: () => void
  side?: 'bottom' | 'left'
  children: React.ReactNode
}) {
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClick}
          aria-label={label}
          data-testid={testId}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent side={side}>{label}</TooltipContent>
    </Tooltip>
  )
}

export function ChatTopBar(props: ChatTopBarProps) {
  const { variant, contentWidth, creditsRemaining } = props
  const isMobile = useIsMobile()
  const isFullscreen = variant === 'fullscreen'
  const [pastOpen, setPastOpen] = useState(false)

  const phoneMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-touch"
          aria-label="Chat options"
          data-testid="chat-overflow-button"
        >
          <MoreHorizontal className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-44">
        <DropdownMenuItem
          className="min-h-11"
          onSelect={props.onClear}
          data-testid="clear-chat-button"
        >
          <SquarePen />
          New chat
        </DropdownMenuItem>
        <DropdownMenuItem
          className="min-h-11"
          onSelect={() => setPastOpen(true)}
          data-testid="past-chats-button"
        >
          <History />
          Past chats
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const inlineControls = (
    <div className="flex items-center gap-0.5">
      <IconAction label="Past chats" testId="past-chats-button" onClick={() => setPastOpen(true)}>
        <History className="size-4" />
      </IconAction>
      <IconAction label="New chat" testId="clear-chat-button" onClick={props.onClear}>
        <SquarePen className="size-4" />
      </IconAction>
      {props.onClose && (
        <IconAction
          label="Close chat panel"
          testId="chat-panel-close"
          onClick={props.onClose}
          side="left"
        >
          <X className="size-4" />
        </IconAction>
      )}
    </div>
  )

  return (
    <div className="border-border/60 h-14 shrink-0 border-b md:h-[60px]" data-testid="chat-top-bar">
      <div className={cn('flex h-full items-center gap-1.5 px-3', contentWidth)}>
        {isFullscreen && <BackToHome segment="agents" />}
        {props.icon}
        {/* A long name truncates rather than pushing the app actions off a phone's screen. */}
        <h2 className="min-w-0 truncate text-base font-semibold">{props.title}</h2>
        {/* The floating panel has no app actions, so it shows the balance itself. */}
        {!isMobile && !isFullscreen && creditsRemaining !== null && (
          <span
            className="text-muted-foreground flex shrink-0 items-center gap-1 text-xs"
            data-testid="credits-display"
          >
            <Coins className="size-3" />
            {creditsRemaining.toLocaleString()} credits
          </span>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {isMobile ? phoneMenu : inlineControls}
          {/* A phone reaches the account from the home screen, one tap back; here it never fit. */}
          {(isMobile || isFullscreen) && <HeaderActions account={false} />}
        </div>
      </div>
      <PastChatsSheet open={pastOpen} onOpenChange={setPastOpen} roleId={props.roleId} />
    </div>
  )
}
